/* eslint-disable @typescript-eslint/no-deprecated -- lucide deprecated its
   brand icons; the whole app still renders <Youtube /> for YouTube
   sources, so swapping it here alone would look inconsistent. */
import { Clapperboard, Film, Scissors, Trash2, Youtube } from 'lucide-react'
import { PageHeader } from '@/components/ds/PageHeader'
import { fmt, pluralize, relativeTime } from '../format'
import { totalClipDuration, type ProjectVideo, type ToolProject } from '../model'
import { VideoImporter } from './VideoImporter'
import styles from '../styles/shell.module.scss'

export interface ProjectsStageProps {
  projects: ToolProject[]
  /** localStorage already read — avoids flashing the empty state. */
  ready: boolean
  onOpen: (projectId: string) => void
  onDelete: (project: ToolProject) => void
  /** A video was imported: start a project around it. */
  onCreate: (video: ProjectVideo) => void
}

/** Landing screen: pick up a project, or import a video to start one. */
export function ProjectsStage({ projects, ready, onOpen, onDelete, onCreate }: ProjectsStageProps) {
  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Tools"
        title="Criador de clipes"
        description="Cada importação vira um projeto: junte vídeos, marque os trechos na linha do tempo e exporte os cortes na ordem que quiser."
      />

      <div className={styles.importStage}>
        <div className={styles.importCard}>
          {ready && projects.length > 0 ? (
            <section className={styles.importsList} aria-label="Projetos">
              <header className={styles.importsHeader}>
                <h3>Seus projetos</h3>
                <span className={styles.importsCount}>
                  {pluralize(projects.length, 'projeto', 'projetos')}
                </span>
              </header>
              <ul className={styles.importsUl}>
                {projects.map((project) => {
                  const clipTime = totalClipDuration(project)
                  return (
                    <li key={project.id} className={styles.importsItem}>
                      <button
                        type="button"
                        className={styles.importsOpen}
                        onClick={() => {
                          onOpen(project.id)
                        }}
                        aria-label={`Abrir ${project.name}`}
                      >
                        <span className={styles.importsIcon} aria-hidden>
                          {project.videos.length > 1 ? (
                            <Clapperboard />
                          ) : project.videos[0]?.origin === 'youtube' ? (
                            <Youtube />
                          ) : (
                            <Film />
                          )}
                        </span>
                        <span className={styles.importsBody}>
                          <span className={styles.importsName}>{project.name}</span>
                          <span className={styles.importsMeta}>
                            {[
                              pluralize(project.videos.length, 'vídeo', 'vídeos'),
                              pluralize(project.clips.length, 'clipe', 'clipes'),
                              clipTime > 0 ? fmt(clipTime) : null,
                              relativeTime(project.updatedAt),
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className={styles.importsDel}
                        onClick={() => {
                          onDelete(project)
                        }}
                        aria-label={`Excluir projeto ${project.name}`}
                        title="Excluir o projeto (os vídeos continuam no servidor)"
                      >
                        <Trash2 aria-hidden />
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ) : ready ? (
            <div className={styles.projectsEmpty}>
              <Scissors aria-hidden />
              <p>Nenhum projeto ainda. Importe um vídeo para começar.</p>
            </div>
          ) : null}

          <VideoImporter onVideo={onCreate} showExisting />
        </div>
      </div>
    </div>
  )
}
